import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import useAppSelector from "@/core/helpers/useAppSelector";
import { messageOpacityClass } from "@/domains/chat/constants/chat";
import {
  currTabChatSelector,
  indexMessageBySearchSelector,
  messagesByTabSelector,
  searchValueSelector,
  uidMessagesBySearchSelector,
} from "@/domains/chat/slice";
import useCopyMessage from "@/domains/chat/hooks/useCopyMessage";
import ChatMessage from "@/domains/chat/components/chat/ChatMessage";
import { appliedChatSettingsSelector } from "@/domains/settings/chat/slice";

type Props = {
  isOpacityMode?: boolean;
};

export default function ChatMessagesContainer({ isOpacityMode }: Props) {
  const ref = useRef(null);
  const { copyMessageHandler } = useCopyMessage();
  const currTab = useAppSelector(currTabChatSelector);
  const uidMessagesBySearch = useAppSelector(uidMessagesBySearchSelector);
  const indexMessageBySearch = useAppSelector(indexMessageBySearchSelector);
  const searchValue = useAppSelector(searchValueSelector);
  const {
    highlightOwnMessages,
    showTime,
    messageHeader,
    showBadge,
    fontSize,
    fontWeight,
    typePaintingMessage,
    typeRankPlayerFormat,
    opacityText,
    autoscroll,
  } = useAppSelector(appliedChatSettingsSelector);
  const messages = useAppSelector((state) => messagesByTabSelector(state, currTab));

  const scrollToBottom = useCallback(() => {
    ref.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [ref]);

  useEffect(() => {
    if (autoscroll) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, autoscroll]);

  const scrollToMessage = useCallback((uid: string) => {
    const message = document.getElementById(uid);
    if (ref.current && message) {
      const container = ref.current;

      container.scrollTo({
        top: message.offsetTop - container.offsetTop,
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  useEffect(() => {
    if (indexMessageBySearch > -1) {
      scrollToMessage(uidMessagesBySearch[indexMessageBySearch]);
    } else {
      scrollToBottom();
    }
  }, [indexMessageBySearch, scrollToBottom, scrollToMessage, uidMessagesBySearch]);

  return (
    <div className="relative grow mt-3.5">
      <div
        ref={ref}
        className={clsx(
          "absolute inset-0 flex flex-col-reverse overflow-y-auto grow gap-1",
          "[--scrollbar-my:0.875rem] [--scrollbar-track-bg:#130F144D] [--scrollbar-thumb-bg:bg-gradient-red] [scrollbar-gutter:stable] [overflow-anchor:auto] [mask-image:linear-gradient(transparent,black_0.875rem,black_calc(100%-0.875rem))]",
          messageOpacityClass[opacityText],
        )}
        id="chat-list"
      >
        {messages.map((message) => {
          const hasSearchValue = uidMessagesBySearch.includes(message.uid);
          return (
            <ChatMessage
              key={message.uid}
              message={message}
              settings={{
                highlightOwnMessages,
                showTime,
                messageHeader,
                showBadge,
                fontSize,
                fontWeight,
                typePaintingMessage,
                typeRankPlayerFormat,
              }}
              isOpacityMode={isOpacityMode}
              onCopy={copyMessageHandler}
              searchValue={hasSearchValue ? searchValue : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}
